import { isBrowser } from '@dx-ui/utilities-is-browser';
import type { EnvKeys, PublicEnv } from './types';

type ServerEnvSource = () => Record<string, string | undefined>;

let serverEnvSource: ServerEnvSource | null = null;

/**
 * Register a custom server-side env source.
 * Call once from your server entry before any rendering begins.
 * Falls back to `process.env` when no source is registered (e.g. Next.js apps).
 */
export function setServerEnvSource(source: ServerEnvSource | null) {
  serverEnvSource = source;
}

/**
 * Isomorphic function that returns all environment variables.
 * @note This is only available on the client when the `EnvScript` component is used.
 */
export function allEnv() {
  return isBrowser ? getClientEnv() : getServerEnv();
}

/**
 * Isomorphic getter that returns a specific environment variable.
 * @note This is only available on the client when the `EnvScript` component is used.
 * @param key
 */
export function env(key: EnvKeys, defaultValue?: string) {
  return allEnv()[key] || defaultValue || '';
}

function getClientEnv() {
  if (
    !window.__ENV &&
    (window as Window & { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__?.buildId ===
      'development'
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      'Environment variables are not available.  Did you forget to include the `EnvScript` component?'
    );
  }
  return window.__ENV || {};
}

/** @internal Exposed for `EnvScript` which always runs server-side. */
export function getServerEnv() {
  if (serverEnvSource) {
    return serverEnvSource() as unknown as PublicEnv;
  }
  return process.env as unknown as PublicEnv;
}
